import * as React from 'react'
import {
  ButtonGhost,
  ButtonPrimary,
  ClickableButtonProps,
  ClickableLinkProps,
  ClickableIconProps,
  IconButtonClose,
} from './buttons'
import { slugify } from './utils'

export type ModalButtonProps = {
  label: string
  isDisabled?: boolean
  onClick?: () => void
} & ClickableIconProps &
  (ClickableButtonProps | ClickableLinkProps)

type ModalProps = {
  children: React.ReactNode
  hasForm?: boolean
  title: string
  titleIsSrText?: boolean
  primaryAction?: ModalButtonProps
  secondaryAction?: ModalButtonProps
  modifier?: 'video' | 'form'
  extraClasses?: string
  extraHeaderJSX?: React.ReactNode
  onHide: () => void
}

const ModalInner = (p: ModalProps) => (
  <>
    <header className="modal__header">
      <h2
        id={`a11y-modal-title-${slugify(p.title)}`}
        className={`modal__title title--xs${p.titleIsSrText ? ' sr-text' : ''}`}
      >
        {p.title}
      </h2>
      {p.extraHeaderJSX}
    </header>

    <div className="modal__content">{p.children}</div>

    {(p.primaryAction || p.secondaryAction) && (
      <footer className="modal__footer block__clickables">
        {p.primaryAction && (
          <div className="modal__clickable block__clickable">
            <ButtonPrimary size="m" {...p.primaryAction} />
          </div>
        )}

        {p.secondaryAction && (
          <div className="modal__clickable block__clickable">
            <ButtonGhost size="m" {...p.secondaryAction} />
          </div>
        )}
      </footer>
    )}
  </>
)

export class ModalLayout extends React.Component<ModalProps> {
  handleEscapeClose(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      document.documentElement.classList.remove('modal--toggled')
      this.props.onHide()
    }
  }

  componentDidMount() {
    document.documentElement.classList.add('modal--toggled')
    // Close modal when pressing the escape key
    document.addEventListener('keyup', (e) => this.handleEscapeClose(e))
  }

  componentWillUnmount() {
    document.documentElement.classList.remove('modal--toggled')
    document.removeEventListener('keyup', (e) => this.handleEscapeClose(e))
  }

  render() {
    const { props } = this
    const { onHide, title, hasForm, modifier } = props
    return (
      <div
        key={`modal-${slugify(title)}`}
        role="dialog"
        aria-labelledby={`a11y-modal-title-${slugify(title)}`}
        aria-modal={true}
        className={`modal${modifier ? ` modal--${modifier}` : ''}`}
      >
        {hasForm ? (
          <form
            className="modal__inner"
            onSubmit={(e) => {
              e.preventDefault()
              if (this.props.primaryAction?.onClick !== undefined)
                this.props.primaryAction.onClick()
            }}
          >
            <ModalInner {...props} />
            <IconButtonClose
              kind="button"
              type="button"
              extraClasses="modal__close"
              onClick={onHide}
            />
          </form>
        ) : (
          <div className="modal__inner">
            <ModalInner {...props} />
            <IconButtonClose
              kind="button"
              extraClasses="modal__close"
              onClick={onHide}
            />
          </div>
        )}
        <div
          aria-hidden="true"
          className="modal__bg"
          onClick={(e) => {
            e.preventDefault()
            onHide()
          }}
        />
      </div>
    )
  }
}
